/**
 * Hero Layout Legend Component
 * 
 * Shows color legend for hero item types.
 * Explains what each color in the preset icons represents.
 */

import React from 'react';
import { ITEM_TYPE_COLORS } from './heroLayoutPresets';
import { surfaces, borders, textColors } from '../../../design-system/tokens';

/**
 * Small icon components for legend
 */
function LegendImageIcon({ color, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="0" y="0" width="16" height="16" rx="2" fill={color} opacity="0.15" />
      {/* Landscape frame */}
      <rect x="2" y="4" width="12" height="8" rx="0.5" fill="none" stroke={color} strokeWidth="0.8" opacity="0.8" />
      {/* Mountain landscape */}
      <path
        d="M 2 12 L 5 8 L 8 10 L 14 6 L 14 12 Z"
        fill={color}
        opacity="0.6"
      />
      {/* Sun */}
      <circle cx="11.5" cy="6" r="1" fill={color} opacity="0.6" />
    </svg>
  );
}

function LegendVideoIcon({ color, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="0" y="0" width="16" height="16" rx="2" fill={color} opacity="0.15" />
      {/* Video frame */}
      <rect x="2" y="5" width="10" height="6" rx="0.5" fill="none" stroke={color} strokeWidth="0.8" opacity="0.8" />
      {/* Play button */}
      <path d="M 6 7 L 6 9 L 8.5 8 Z" fill={color} opacity="0.7" />
    </svg>
  );
}

function LegendFileIcon({ color, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="0" y="0" width="16" height="16" rx="2" fill={color} opacity="0.15" />
      {/* File document outline */}
      <path
        d="M 4 2 L 9 2 L 12 5 L 12 14 L 4 14 Z"
        fill="none"
        stroke={color}
        strokeWidth="0.8"
        opacity="0.8"
      />
      {/* Folded corner */}
      <path d="M 9 2 L 9 4.5 L 12 5 Z" fill="none" stroke={color} strokeWidth="0.8" opacity="0.8" />
      {/* Document lines */}
      <line x1="5.5" y1="7" x2="10.5" y2="7" stroke={color} strokeWidth="0.6" opacity="0.6" />
      <line x1="5.5" y1="9" x2="10.5" y2="9" stroke={color} strokeWidth="0.6" opacity="0.6" />
      <line x1="5.5" y1="11" x2="9" y2="11" stroke={color} strokeWidth="0.6" opacity="0.6" />
    </svg>
  );
}

function LegendButtonIcon({ color, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="0" y="0" width="16" height="16" rx="2" fill={color} opacity="0.15" />
      {/* Three small buttons - outline only */}
      <rect x="2" y="6" width="3" height="4" rx="0.8" fill="none" stroke={color} strokeWidth="0.8" opacity="0.8" />
      <rect x="6.5" y="6" width="3" height="4" rx="0.8" fill="none" stroke={color} strokeWidth="0.8" opacity="0.8" />
      <rect x="11" y="6" width="3" height="4" rx="0.8" fill="none" stroke={color} strokeWidth="0.8" opacity="0.8" />
    </svg>
  );
}

function LegendTitleIcon({ color, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="0" y="0" width="16" height="16" rx="2" fill={color} opacity="0.15" />
      <text
        x="8"
        y="11"
        fontSize="7"
        fontWeight="600"
        fill={color}
        textAnchor="middle"
      >
        Title
      </text>
    </svg>
  );
}

function LegendTextIcon({ color, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="0" y="0" width="16" height="16" rx="2" fill={color} opacity="0.15" />
      <text
        x="8"
        y="11"
        fontSize="5.5"
        fontWeight="400"
        fill={color}
        textAnchor="middle"
      >
        Text
      </text>
    </svg>
  );
}

function getLegendIcon(type, color, size) {
  switch (type) {
    case 'image':
      return <LegendImageIcon color={color} size={size} />;
    case 'video':
      return <LegendVideoIcon color={color} size={size} />;
    case 'file':
      return <LegendFileIcon color={color} size={size} />;
    case 'button_collection':
      return <LegendButtonIcon color={color} size={size} />;
    case 'title':
      return <LegendTitleIcon color={color} size={size} />;
    case 'text':
      return <LegendTextIcon color={color} size={size} />;
    default:
      return null;
  }
}

/**
 * Shows color legend for hero item types
 * @param {boolean} compact - Whether to show compact version
 */
export default function HeroLayoutLegend({ compact = false }) {
  const items = [
    { type: 'image', label: 'Image' },
    { type: 'title', label: 'Title' },
    { type: 'text', label: 'Text' },
    { type: 'button_collection', label: 'Buttons' },
    { type: 'video', label: 'Video' },
    { type: 'file', label: 'File' }
  ];
  
  if (compact) {
    return (
      <div className={`flex flex-wrap items-center gap-2 text-xs ${textColors.tertiary} mb-2`}>
        {items.map(({ type, label }) => (
          <div key={type} className="flex items-center gap-1">
            {getLegendIcon(type, ITEM_TYPE_COLORS[type], 12)}
            <span className="ml-0.5">{label}</span>
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className={`${surfaces.tertiary} border ${borders.default} rounded-md p-3 mb-3`}>
      <h4 className={`text-xs font-semibold ${textColors.secondary} mb-2`}>Item Types:</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map(({ type, label }) => (
          <div key={type} className={`flex items-center gap-2 text-sm ${textColors.tertiary}`}>
            {getLegendIcon(type, ITEM_TYPE_COLORS[type], 16)}
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

